/**
 * @fileoverview Connect staff navigation to scheduling, queue, and durable-integration dashboards.
 *
 * Child screens own status observations and explicit controls. Activation choice,
 * queued work, execution state, and acknowledged provider effects are distinct
 * operational facts.
 *
 * `kc-internal-backend/functions/scheduled_jobs.py` owns job choices;
 * `kc-internal-backend/functions/pipedrive_webhooks.py` owns provider ingress;
 * `kc-internal-backend/functions/client_won.py` owns onboarding. URL tab state
 * connects these views without becoming an activation or backfill trigger.
 * New controls must retain the owning workflow's admission, retry, and stopping
 * boundaries.
 */

import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { AppTabView, AppTab } from '../shared/AppTabView';
import BankingSubmissions from './BankingSubmissions';
import AICalendarPipeline from './AICalendarPipeline';
import ScheduledJobs from './ScheduledJobs';
import CeleryDashboard from './CeleryDashboard';
import PipedriveWebhooks from './PipedriveWebhooks';
import JamsDocuments from './JamsDocuments';
import ClientWonEvents from './ClientWonEvents';
import MatterContactReminders from './MatterContactReminders';
import SlackMessages from '../Admin/SlackMessages';
import { AppPageHeader } from '../shared';
import useUrlTabState from '../shared/useUrlTabState';
import { AuthContext } from '../../AuthContext';
import './automations.css';

const AUTOMATIONS_TAB_IDS = [
    'scheduled-jobs',
    'celery',
    'pipedrive-webhooks',
    'banking-submissions',
    'ai-calendar',
    'jams-documents',
    'client-won',
    'matter-contact-reminders',
    'slack-messages',
];



function Automations() {
    const { isAdmin = false } = useContext(AuthContext) || {};
    const tabIds = useMemo(() => AUTOMATIONS_TAB_IDS.filter(id => isAdmin || id !== 'celery'), [isAdmin]);
    const { activeIndex: activeTab, onTabChange } = useUrlTabState(tabIds);

    const setAutomationsDocumentTitle = useCallback(

    () => {
        document.title = "Automations";
    }, []);

    useEffect(setAutomationsDocumentTitle, [setAutomationsDocumentTitle]);

    return (
        <div className="automations-page">
            <AppTabView
                activeIndex={activeTab}
                onTabChange={onTabChange}
                variant="route"
                className="automations-page__tabs"
                header={
                    <AppPageHeader
                        embedded
                        eyebrow="Workflow Control"
                        title="Automations"
                        icon="pi pi-bolt text-xl"
                    />
                }
            >
                <AppTab header="Scheduled Jobs">
                    <ScheduledJobs />
                </AppTab>
                {isAdmin && <AppTab header="Celery">
                    <CeleryDashboard />
                </AppTab>}
                <AppTab header="Webhooks">
                    <PipedriveWebhooks />
                </AppTab>
                <AppTab header="Banking Submissions">
                    <BankingSubmissions />
                </AppTab>
                <AppTab header="AI Calendar">
                    <AICalendarPipeline />
                </AppTab>
                <AppTab header="JAMS Documents">
                    <JamsDocuments />
                </AppTab>
                <AppTab header="Client Won">
                    <ClientWonEvents />
                </AppTab>
                <AppTab header="Matter Contact Reminders">
                    <MatterContactReminders />
                </AppTab>
                <AppTab header="Slack Messages">
                    <SlackMessages embedded />
                </AppTab>
            </AppTabView>
        </div>
    );
}

export default Automations;
